/*
 * Top page comes last: on a phone the table scrolls sideways with the query pinned, and the
 * figures are what a reader compares down the column, so they come first.
 *
 * What people searched for when Google showed our pages: the last 28 days of Search Console
 * queries, compared with the 28 before. Server-paged and server-sorted, because the ranking is
 * over every query and a page of 50 cannot sort itself honestly.
 */
import EmptyState from '@components/Common/EmptyState';
import ErrorMessage from '@components/Common/ErrorMessage';
import FilterBar, { type AppliedFilter } from '@components/Common/FilterBar';
import MetricTable, { type MetricColumn } from '@components/Common/MetricTable';
import RecordLink from '@components/Common/RecordLink';
import SearchInput from '@components/Common/SearchInput';
import SelectFormField from '@components/Common/SelectFormField';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import { Box, TablePagination } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import ChangeValue from './ChangeValue';
import {
  type KeywordSort,
  type KeywordsState,
  PAGE_SIZE,
  type SeoKeyword,
  type SeoKeywordPage,
  type SeoSummary,
  clicksChange,
  fetchKeywords,
  fmtCount,
  fmtCtr,
  fmtPosition,
  pageIdOf,
  positionChange,
  rangeLabel,
} from './seoData';

const MIN_IMPRESSION_OPTIONS = [
  { value: '0', label: 'Any' },
  { value: '10', label: '10 or more' },
  { value: '50', label: '50 or more' },
  { value: '100', label: '100 or more' },
  { value: '500', label: '500 or more' },
];

/** The direction each server sort runs in, so the header arrow tells the truth. */
const SORT_DIR: Record<KeywordSort, 'asc' | 'desc'> = {
  clicks: 'desc',
  impressions: 'desc',
  ctr: 'desc',
  position: 'asc',
  change: 'desc',
  query: 'asc',
};

const COLUMNS: MetricColumn<SeoKeyword>[] = [
  {
    key: 'query',
    header: 'Query',
    variant: 'name',
    serverSort: true,
    render: (k) => k.query,
  },
  {
    key: 'clicks',
    header: 'Clicks',
    variant: 'strong',
    serverSort: true,
    render: (k) => fmtCount(k.clicks),
  },
  {
    key: 'impressions',
    header: 'Impressions',
    serverSort: true,
    render: (k) => fmtCount(k.impressions),
  },
  {
    key: 'ctr',
    header: 'CTR',
    tooltip: 'Clicks divided by impressions',
    serverSort: true,
    render: (k) => fmtCtr(k.ctr),
  },
  {
    key: 'position',
    header: 'Avg position',
    tooltip: 'Average position in Google results; 1 is the top',
    serverSort: true,
    render: (k) => fmtPosition(k.position),
  },
  {
    key: 'change',
    header: 'Change',
    tooltip:
      'Clicks and position against the previous 28 days. A rise in position means it moved up.',
    serverSort: true,
    render: (k) => (
      <span className="seo-change-pair">
        <ChangeValue change={clicksChange(k.clicksChange)} label="Clicks" />
        <ChangeValue
          change={positionChange(k.positionChange)}
          label="Position"
        />
      </span>
    ),
  },
  {
    key: 'topPage',
    header: 'Top page',
    align: 'left',
    tooltip: 'The page with the most clicks for this query',
    render: (k) =>
      k.topPage ? (
        <RecordLink
          to={`/seo/pages/${pageIdOf(k.topPage)}`}
          label={`Open ${k.topPage}`}
        >
          {k.topPage}
        </RecordLink>
      ) : (
        '-'
      ),
  },
];

const SeoKeywordsTab: React.FC<{
  state: KeywordsState;
  setState: (s: KeywordsState) => void;
  summary: SeoSummary | null;
  reloadKey: number;
}> = ({ state, setState, summary, reloadKey }) => {
  const [qDraft, setQDraft] = useState(state.q);
  const [data, setData] = useState<SeoKeywordPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setQDraft(state.q), [state.q]);

  const latest = useRef(0);
  const load = useCallback(async () => {
    const call = ++latest.current;
    setLoading(true);
    setError(null);
    try {
      const r = await fetchKeywords(state);
      if (call === latest.current) setData(r);
    } catch {
      if (call === latest.current)
        setError('The keywords could not be loaded.');
    } finally {
      if (call === latest.current) setLoading(false);
    }
  }, [state]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const applied: AppliedFilter[] = [
    state.q && {
      key: 'kq',
      label: `Search: ${state.q}`,
      onClear: () => setState({ ...state, q: '', page: 0 }),
    },
    state.minImpressions > 0 && {
      key: 'kmin',
      label: `Impressions: ${state.minImpressions} or more`,
      onClear: () => setState({ ...state, minImpressions: 0, page: 0 }),
    },
  ].filter(Boolean) as AppliedFilter[];

  const rows = data?.content ?? [];
  const total = data?.totalElements ?? 0;
  const neverSynced = !summary?.searchSyncedAt;
  const range = rangeLabel(data?.from, data?.to);

  return (
    <Box className="seo-list__tab">
      <FilterBar
        applied={applied}
        onApply={() => setState({ ...state, q: qDraft.trim(), page: 0 })}
        onClearAll={() => {
          setQDraft('');
          setState({ ...state, q: '', minImpressions: 0, page: 0 });
        }}
        onCancel={() => setQDraft(state.q)}
      >
        <Box className="seo-list__search">
          <SearchInput
            value={qDraft}
            onChange={setQDraft}
            placeholder="Search queries"
            onClear={() => setQDraft('')}
            onKeyDown={(e) => {
              if (e.key === 'Enter')
                setState({ ...state, q: qDraft.trim(), page: 0 });
            }}
          />
        </Box>
        <SelectFormField
          required={false}
          name="seo-min-impressions"
          label="Impressions"
          options={MIN_IMPRESSION_OPTIONS}
          value={String(state.minImpressions)}
          onChange={(_n, v) =>
            setState({ ...state, minImpressions: Number(v) || 0, page: 0 })
          }
        />
      </FilterBar>

      {range && (
        <p className="seo-list__range">
          Last 28 days, {range}. Change is against the 28 days before.
        </p>
      )}

      {loading ? (
        <SkeletonLoader rows={10} columns={7} />
      ) : error ? (
        <ErrorMessage message={error} onRetry={load} />
      ) : rows.length === 0 ? (
        <EmptyState
          title={
            neverSynced
              ? 'Google data appears after the first sync'
              : applied.length
                ? 'No queries match these filters'
                : 'No search queries in the last 28 days'
          }
          body={
            neverSynced
              ? 'Use "Refresh Google data" to read Search Console for the first time.'
              : applied.length
                ? 'Clear a filter to see more.'
                : 'Queries appear once Google shows a tracked page in its results.'
          }
        />
      ) : (
        <Box className="seo-list__keywords">
          <MetricTable<SeoKeyword>
            columns={COLUMNS}
            rows={rows}
            getRowKey={(k) => k.query}
            sort={{ key: state.sort, dir: SORT_DIR[state.sort] }}
            onSortChange={(key) =>
              setState({ ...state, sort: key as KeywordSort, page: 0 })
            }
          />
          {total > PAGE_SIZE && (
            <TablePagination
              component="div"
              count={total}
              page={state.page}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
              onPageChange={(_e, page) => setState({ ...state, page })}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default SeoKeywordsTab;
